import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Supported languages
export type Language = 'sr' | 'en';

// Translation keys structure
export interface Translations {
  // Navigation
  nav: {
    overview: string;
    programs: string;
    controls: string;
    risks: string;
    assets: string;
    policies: string;
    suppliers: string;
    audits: string;
    incidents: string;
    analytics: string;
    settings: string;
    proof: string;
    integrations: string;
    ai_assistant: string;
    // Enterprise modules
    training: string;
    automation: string;
    cloud: string;
    saas: string;
    endpoints: string;
    pentest: string;
    trustCenter: string;
    aiAssistant: string;
    executive: string;
  };
  // Common actions
  actions: {
    save: string;
    cancel: string;
    delete: string;
    edit: string;
    add: string;
    create: string;
    update: string;
    search: string;
    filter: string;
    export: string;
    import: string;
    download: string;
    upload: string;
    back: string;
    next: string;
    finish: string;
    close: string;
    confirm: string;
    apply: string;
    reset: string;
    clear: string;
    select: string;
    selectAll: string;
    view: string;
    details: string;
    generatePolicy: string;
    linkEvidence: string;
    runAnalysis: string;
    remove: string;
    description: string;
    showAll: string;
    showLess: string;
    fix: string;
    required: string;
  };
  // Status labels
  status: {
    compliant: string;
    partial: string;
    nonCompliant: string;
    notAssessed: string;
    notApplicable: string;
    implemented: string;
    inProgress: string;
    notStarted: string;
    completed: string;
    healthy: string;
    atRisk: string;
    critical: string;
    active: string;
    inactive: string;
    open: string;
    closed: string;
    mitigated: string;
    accepted: string;
  };
  // Dashboard
  dashboard: {
    title: string;
    welcome: string;
    totalControls: string;
    applicable: string;
    partial: string;
    notApplicable: string;
    complianceScore: string;
    healthStatus: string;
    implementation: string;
    testing: string;
    freshness: string;
    recentActivity: string;
    nextActions: string;
    aiAssistant: string;
    askAnything: string;
    topRisks: string;
    upcomingAudits: string;
    noOpenRisks: string;
    noScheduledAudits: string;
    soa: string;
    automatedChecks: string;
    manualChecks: string;
    aiAssessment: string;
    recommendations: string;
    noSummaryAvailable: string;
    priorityHigh: string;
    priorityMedium: string;
    priorityLow: string;
  };
  // Programs/Frameworks
  programs: {
    title: string;
    addNew: string;
    selectFramework: string;
    name: string;
    health: string;
    definition: string;
    owner: string;
    createdDate: string;
    requirements: string;
    controls: string;
    labels: string;
    proofLinks: string;
    startQuestionnaire: string;
    viewDetails: string;
  };
  // Controls
  controls: {
    title: string;
    code: string;
    description: string;
    guidance: string;
    severity: string;
    checkType: string;
    implementationStatus: string;
    testingStatus: string;
    owner: string;
    linkedPolicies: string;
    evidence: string;
    lastReview: string;
    nextReview: string;
    applicability: string;
    justification: string;
    automated: string;
    manual: string;
    hybrid: string;
    linkPolicyAsEvidence: string;
    noPoliciesAvailable: string;
  };
  // Risks
  risks: {
    title: string;
    register: string;
    summary: string;
    residualRisk: string;
    riskHealth: string;
    heatmap: string;
    likelihood: string;
    impact: string;
    treatment: string;
    inherentRisk: string;
    toleranceLevel: string;
    veryHigh: string;
    high: string;
    moderate: string;
    low: string;
    veryLow: string;
  };
  // Assets
  assets: {
    title: string;
    inventory: string;
    name: string;
    type: string;
    category: string;
    value: string;
    location: string;
    custodian: string;
    classification: string;
  };
  // Policies
  policies: {
    title: string;
    generate: string;
    template: string;
    version: string;
    approvedBy: string;
    effectiveDate: string;
    reviewDate: string;
    linkedControls: string;
    attachAsEvidence: string;
  };
  // Audits
  audits: {
    title: string;
    name: string;
    type: string;
    timeline: string;
    requests: string;
    findings: string;
    internal: string;
    external: string;
    certification: string;
    surveillance: string;
    aiPowered: string;
    startDeepAudit: string;
    analyzeAndGenerate: string;
    history: string;
    noAuditsYet: string;
    executiveSummary: string;
    recommendations: string;
    recommendation: string;
    comments: string;
    notes: string;
    noCommentsYet: string;
    addComment: string;
    scheduledReviews: string;
    deepAuditProgress: string;
  };
  // Suppliers
  suppliers: {
    title: string;
    management: string;
    name: string;
    riskLevel: string;
    contractDate: string;
    reviewDate: string;
    services: string;
    certifications: string;
    dueDiligence: string;
  };
  // Incidents
  incidents: {
    title: string;
    report: string;
    severity: string;
    reportedDate: string;
    resolvedDate: string;
    affectedAssets: string;
    rootCause: string;
    lessonsLearned: string;
  };
  // Questionnaire
  questionnaire: {
    title: string;
    welcome: string;
    applicabilityCheck: string;
    questionOf: string;
    progress: string;
    selectOption: string;
    yes: string;
    no: string;
    notSure: string;
    skip: string;
    results: string;
    applicableControls: string;
    recommendations: string;
    complete: string;
    excludedControls: string;
    viewResults: string;
    applyRecommendations: string;
  };
  // Penalties
  penalties: {
    title: string;
    warning: string;
    potentialFines: string;
    maxFine: string;
    legalBasis: string;
    nonCompliantControls: string;
    resolveNow: string;
    riskLevel: string;
  };
  // Settings
  settings: {
    title: string;
    users: string;
    groups: string;
    permissions: string;
    auditTrail: string;
    language: string;
    theme: string;
    notifications: string;
    integrations: string;
  };
  // AI
  ai: {
    title: string;
    analysis: string;
    recommendations: string;
    generateReport: string;
    askQuestion: string;
    priority: string;
    confidence: string;
  };
  // Freshness
  freshness: {
    fresh: string;
    expired: string;
    unknown: string;
    notSet: string;
    daysAgo: string;
    daysRemaining: string;
  };
  // Explore panel
  explore: {
    title: string;
    scopes: string;
    members: string;
    domains: string;
    priority: string;
  };
  // Proof/Evidence
  proof: {
    title: string;
    addProof: string;
    uploadFile: string;
    linkPolicy: string;
    linkControl: string;
    freshUntil: string;
    uploadedBy: string;
    uploadedOn: string;
    linkEvidence: string;
    policies: string;
    existingEvidence: string;
    uploadNew: string;
    selectFile: string;
    evidenceType: string;
    uploadEvidence: string;
    linkSelected: string;
    linkedEvidence: string;
    manage: string;
    manageEvidence: string;
  };
  // Analytics
  analytics: {
    title: string;
    overview: string;
    comparison: string;
    maturity: string;
    refresh: string;
    export: string;
    overallCompliance: string;
    averageAcross: string;
    activeFrameworks: string;
    controlsImplemented: string;
    pendingActions: string;
    globalStatus: string;
    complianceByFramework: string;
    controlsBreakdown: string;
    multiDimensional: string;
    maturityAssessment: string;
    priorityRecommendations: string;
    noData: string;
    completeWizard: string;
    implemented: string;
    inProgress: string;
    notStarted: string;
    level: string;
    optimized: string;
    managed: string;
    defined: string;
    developing: string;
    initial: string;
  };
  // AI Advisor
  aiAdvisor: {
    title: string;
    analyzing: string;
    technicalControls: string;
    organizationalControls: string;
    implementationSteps: string;
    requiredDocumentation: string;
    suggestedJustification: string;
    suggestedOwner: string;
    dismiss: string;
    applySuggestions: string;
    error: string;
    close: string;
    notApplicable: string;
  };
  // RBAC / User Management
  rbac: {
    title: string;
    users: string;
    roles: string;
    permissions: string;
    auditLog: string;
    addUser: string;
    editUser: string;
    deleteUser: string;
    userName: string;
    email: string;
    role: string;
    lastLogin: string;
    status: string;
    actions: string;
    admin: string;
    editor: string;
    viewer: string;
    auditor: string;
    activity: string;
    timestamp: string;
    user: string;
    action: string;
    details: string;
    ipAddress: string;
  };
  // Suppliers extended
  suppliersExt: {
    totalSuppliers: string;
    active: string;
    criticalTier: string;
    nonCompliant: string;
    expiringContract: string;
    searchSuppliers: string;
    allCategories: string;
    allRiskTiers: string;
    allStatuses: string;
    addSupplier: string;
    noSuppliers: string;
    showing: string;
  };
  // Common
  common: {
    of: string;
    showing: string;
    noResults: string;
    loading: string;
    error: string;
    success: string;
    warning: string;
    info: string;
    clickToEdit: string;
    total: string;
    items: string;
    confirmDelete: string;
    date: string;
    actions: string;
  };
}

// Serbian translations
const sr: Translations = {
  nav: {
    overview: 'Pregled',
    programs: 'Programi',
    controls: 'Kontrole',
    risks: 'Rizici',
    assets: 'Imovina',
    policies: 'Politike',
    suppliers: 'Dobavljači',
    audits: 'Revizije',
    incidents: 'Incidenti',
    analytics: 'Analitika',
    settings: 'Podešavanja',
    proof: 'Dokazi',
    integrations: 'Integracije',
    ai_assistant: 'AI Asistent',
    // Enterprise modules
    training: 'Obuke',
    automation: 'Automatizacija',
    cloud: 'Cloud sigurnost',
    saas: 'SaaS sigurnost',
    endpoints: 'Krajnje tačke',
    pentest: 'Pentest',
    trustCenter: 'Trust centar',
    aiAssistant: 'GRC Asistent',
    executive: 'Izvršni izvještaj',
  },
  actions: {
    save: 'Sačuvaj',
    cancel: 'Otkaži',
    delete: 'Obriši',
    edit: 'Izmeni',
    add: 'Dodaj',
    create: 'Kreiraj',
    update: 'Ažuriraj',
    search: 'Pretraži',
    filter: 'Filtriraj',
    export: 'Izvezi',
    import: 'Uvezi',
    download: 'Preuzmi',
    upload: 'Otpremi',
    back: 'Nazad',
    next: 'Dalje',
    finish: 'Završi',
    close: 'Zatvori',
    confirm: 'Potvrdi',
    apply: 'Primeni',
    reset: 'Resetuj',
    clear: 'Obriši',
    select: 'Izaberi',
    selectAll: 'Izaberi sve',
    view: 'Pregledaj',
    details: 'Detalji',
    generatePolicy: 'Generiši politiku',
    linkEvidence: 'Poveži dokaz',
    runAnalysis: 'Pokreni analizu',
    remove: 'Ukloni',
    description: 'Opis',
    showAll: 'Prikaži sve',
    showLess: 'Prikaži manje',
    fix: 'Popravi',
    required: 'Potrebno',
  },
  status: {
    compliant: 'Usklađeno',
    partial: 'Delimično',
    nonCompliant: 'Neusklađeno',
    notAssessed: 'Nije procenjeno',
    notApplicable: 'Nije primenjivo',
    implemented: 'Implementirano',
    inProgress: 'U toku',
    notStarted: 'Nije započeto',
    completed: 'Završeno',
    healthy: 'Zdravo',
    atRisk: 'Rizično',
    critical: 'Kritično',
    active: 'Aktivno',
    inactive: 'Neaktivno',
    open: 'Otvoreno',
    closed: 'Zatvoreno',
    mitigated: 'Ublaženo',
    accepted: 'Prihvaćeno',
  },
  dashboard: {
    title: 'Kontrolna tabla',
    welcome: 'Dobrodošli',
    totalControls: 'Ukupno kontrola',
    applicable: 'Primenjivo',
    partial: 'Delimično',
    notApplicable: 'Nije primenjivo',
    complianceScore: 'Stepen usklađenosti',
    healthStatus: 'Zdravstveni status',
    implementation: 'Implementacija',
    testing: 'Testiranje',
    freshness: 'Svežina',
    recentActivity: 'Nedavna aktivnost',
    nextActions: 'Sledeće akcije',
    aiAssistant: 'AI Asistent',
    askAnything: 'Pitajte bilo šta...',
    topRisks: 'Top rizici',
    upcomingAudits: 'Predstojeći auditi',
    noOpenRisks: 'Nema otvorenih rizika',
    noScheduledAudits: 'Nema zakazanih audita',
    soa: 'Izjava o primenjivosti',
    automatedChecks: 'Automatske provere',
    manualChecks: 'Manualne provere',
    aiAssessment: 'AI Procena',
    recommendations: 'Preporuke',
    noSummaryAvailable: 'Nema dostupnog sažetka.',
    priorityHigh: 'VISOK',
    priorityMedium: 'SREDNJI',
    priorityLow: 'NIZAK',
  },
  programs: {
    title: 'Programi',
    addNew: 'Dodaj novi program',
    selectFramework: 'Izaberi standard/regulativu',
    name: 'Naziv',
    health: 'Zdravlje',
    definition: 'Definicija',
    owner: 'Vlasnik',
    createdDate: 'Datum kreiranja',
    requirements: 'Zahtevi',
    controls: 'Kontrole',
    labels: 'Oznake',
    proofLinks: 'Veze dokaza',
    startQuestionnaire: 'Započni upitnik',
    viewDetails: 'Prikaži detalje',
  },
  controls: {
    title: 'Kontrole',
    code: 'Šifra',
    description: 'Opis',
    guidance: 'Smernice',
    severity: 'Ozbiljnost',
    checkType: 'Tip provere',
    implementationStatus: 'Status implementacije',
    testingStatus: 'Status testiranja',
    owner: 'Vlasnik',
    linkedPolicies: 'Povezane politike',
    evidence: 'Dokazi',
    lastReview: 'Poslednja revizija',
    nextReview: 'Sledeća revizija',
    applicability: 'Primenjivost',
    justification: 'Obrazloženje',
    automated: 'Automatizovano',
    manual: 'Manuelno',
    hybrid: 'Hibridno',
    linkPolicyAsEvidence: 'Poveži politiku kao dokaz',
    noPoliciesAvailable: 'Nema dostupnih politika. Kreirajte u Generatoru politika.',
  },
  risks: {
    title: 'Rizici',
    register: 'Registar rizika',
    summary: 'Pregled rizika',
    residualRisk: 'Rezidualni rizik',
    riskHealth: 'Zdravlje rizika',
    heatmap: 'Mapa rizika',
    likelihood: 'Verovatnoća',
    impact: 'Uticaj',
    treatment: 'Tretman',
    inherentRisk: 'Inherentni rizik',
    toleranceLevel: 'Nivo tolerancije',
    veryHigh: 'Vrlo visok',
    high: 'Visok',
    moderate: 'Umeren',
    low: 'Nizak',
    veryLow: 'Vrlo nizak',
  },
  assets: {
    title: 'Imovina',
    inventory: 'Inventar imovine',
    name: 'Naziv',
    type: 'Tip',
    category: 'Kategorija',
    value: 'Vrednost',
    location: 'Lokacija',
    custodian: 'Staratelj',
    classification: 'Klasifikacija',
  },
  policies: {
    title: 'Politike',
    generate: 'Generiši politiku',
    template: 'Šablon',
    version: 'Verzija',
    approvedBy: 'Odobrio',
    effectiveDate: 'Datum stupanja na snagu',
    reviewDate: 'Datum revizije',
    linkedControls: 'Povezane kontrole',
    attachAsEvidence: 'Priloži kao dokaz',
  },
  audits: {
    title: 'Revizije',
    name: 'Naziv',
    type: 'Tip',
    timeline: 'Vremenski okvir',
    requests: 'Zahtevi',
    findings: 'Nalazi',
    internal: 'Interna',
    external: 'Eksterna',
    certification: 'Sertifikaciona',
    surveillance: 'Nadzorna',
    aiPowered: 'AI Potpomognuto',
    startDeepAudit: 'Pokreni detaljnu reviziju',
    analyzeAndGenerate: 'Analiziraj i generiši',
    history: 'Istorija revizija',
    noAuditsYet: 'Još nema izvršenih revizija.',
    executiveSummary: 'Izvršni sažetak',
    recommendations: 'Preporuke',
    recommendation: 'Preporuka',
    comments: 'Komentari',
    notes: 'Beleške',
    noCommentsYet: 'Nema komentara.',
    addComment: 'Dodaj komentar...',
    scheduledReviews: 'Zakazane revizije',
    deepAuditProgress: 'Napredak detaljna revizije',
  },
  suppliers: {
    title: 'Dobavljači',
    management: 'Upravljanje dobavljačima',
    name: 'Naziv',
    riskLevel: 'Nivo rizika',
    contractDate: 'Datum ugovora',
    reviewDate: 'Datum revizije',
    services: 'Usluge',
    certifications: 'Sertifikati',
    dueDiligence: 'Provera podobnosti',
  },
  incidents: {
    title: 'Incidenti',
    report: 'Prijava incidenta',
    severity: 'Ozbiljnost',
    reportedDate: 'Datum prijave',
    resolvedDate: 'Datum rešavanja',
    affectedAssets: 'Pogođena imovina',
    rootCause: 'Osnovni uzrok',
    lessonsLearned: 'Naučene lekcije',
  },
  questionnaire: {
    title: 'Upitnik',
    welcome: 'Dobrodošli u upitnik za procenu primenjivosti',
    applicabilityCheck: 'Provera primenjivosti',
    questionOf: 'Pitanje {current} od {total}',
    progress: 'Napredak',
    selectOption: 'Izaberite opciju',
    yes: 'Da',
    no: 'Ne',
    notSure: 'Nisam siguran/na',
    skip: 'Preskoči',
    results: 'Rezultati',
    applicableControls: 'Primenjive kontrole',
    recommendations: 'Preporuke',
    complete: 'Završeno',
    excludedControls: 'Isključene kontrole',
    viewResults: 'Prikaži rezultate',
    applyRecommendations: 'Primeni preporuke',
  },
  penalties: {
    title: 'Kazne',
    warning: 'Upozorenje: Potencijalne kazne',
    potentialFines: 'Potencijalne novčane kazne',
    maxFine: 'Maksimalna kazna',
    legalBasis: 'Pravni osnov',
    nonCompliantControls: 'Neusklađene kontrole',
    resolveNow: 'Reši odmah',
    riskLevel: 'Nivo rizika',
  },
  settings: {
    title: 'Podešavanja',
    users: 'Korisnici',
    groups: 'Grupe',
    permissions: 'Dozvole',
    auditTrail: 'Revizijski trag',
    language: 'Jezik',
    theme: 'Tema',
    notifications: 'Obaveštenja',
    integrations: 'Integracije',
  },
  ai: {
    title: 'AI Analiza',
    analysis: 'Analiza',
    recommendations: 'Preporuke',
    generateReport: 'Generiši izveštaj',
    askQuestion: 'Postavi pitanje',
    priority: 'Prioritet',
    confidence: 'Pouzdanost',
  },
  freshness: {
    fresh: 'Sveže',
    expired: 'Isteklo',
    unknown: 'Nepoznato',
    notSet: 'Nije postavljeno',
    daysAgo: 'pre {days} dana',
    daysRemaining: 'ostalo {days} dana',
  },
  explore: {
    title: 'Istraži',
    scopes: 'Opsezi',
    members: 'Članovi',
    domains: 'Domeni',
    priority: 'Prioritet',
  },
  proof: {
    title: 'Dokazi',
    addProof: 'Dodaj dokaz',
    uploadFile: 'Otpremi fajl',
    linkPolicy: 'Poveži politiku',
    linkControl: 'Poveži kontrolu',
    freshUntil: 'Sveže do',
    uploadedBy: 'Otpremio',
    uploadedOn: 'Datum otpreme',
    linkEvidence: 'Poveži dokaz',
    policies: 'Politike',
    existingEvidence: 'Postojeći dokazi',
    uploadNew: 'Otpremi novi',
    selectFile: 'Izaberi fajl',
    evidenceType: 'Tip dokaza',
    uploadEvidence: 'Otpremi dokaz',
    linkSelected: 'Poveži izabrano',
    linkedEvidence: 'Povezani dokazi',
    manage: 'Upravljaj',
    manageEvidence: 'Upravljaj dokazima',
  },
  analytics: {
    title: 'GRC Analitika',
    overview: 'Pregled',
    comparison: 'Poređenje',
    maturity: 'Zrelost',
    refresh: 'Osveži',
    export: 'Izvezi',
    overallCompliance: 'Ukupna usklađenost',
    averageAcross: 'Prosek svih okvira',
    activeFrameworks: 'Aktivni okviri',
    controlsImplemented: 'Implementirane kontrole',
    pendingActions: 'Akcije na čekanju',
    globalStatus: 'Globalni status implementacije',
    complianceByFramework: 'Usklađenost po okviru',
    controlsBreakdown: 'Pregled kontrola po okviru',
    multiDimensional: 'Višedimenzionalno poređenje okvira',
    maturityAssessment: 'Procena zrelosti usklađenosti',
    priorityRecommendations: 'Prioritetne preporuke',
    noData: 'Nema dostupnih analitičkih podataka',
    completeWizard: 'Završite AI upitnik za primenjivost za najmanje jedan okvir da vidite analitiku.',
    implemented: 'Implementirano',
    inProgress: 'U toku',
    notStarted: 'Nije započeto',
    level: 'Nivo',
    optimized: 'Optimizovano',
    managed: 'Upravljano',
    defined: 'Definisano',
    developing: 'U razvoju',
    initial: 'Početno',
  },
  aiAdvisor: {
    title: 'AI Savetnik za implementaciju',
    analyzing: 'AI Savetnik analizira kontrolu',
    technicalControls: 'Tehničke kontrole',
    organizationalControls: 'Organizacione kontrole',
    implementationSteps: 'Koraci implementacije',
    requiredDocumentation: 'Potrebna dokumentacija',
    suggestedJustification: 'Predloženo obrazloženje',
    suggestedOwner: 'Predloženi vlasnik',
    dismiss: 'Odbaci',
    applySuggestions: 'Primeni predloge',
    error: 'Greška',
    close: 'Zatvori',
    notApplicable: 'Nije direktno primenjivo; ova kontrola se fokusira na liderstvo i upravljačku posvećenost umesto na specifične tehničke mere.',
  },
  rbac: {
    title: 'Upravljanje korisnicima',
    users: 'Korisnici',
    roles: 'Uloge',
    permissions: 'Dozvole',
    auditLog: 'Revizijski dnevnik',
    addUser: 'Dodaj korisnika',
    editUser: 'Izmeni korisnika',
    deleteUser: 'Obriši korisnika',
    userName: 'Korisničko ime',
    email: 'Email',
    role: 'Uloga',
    lastLogin: 'Poslednja prijava',
    status: 'Status',
    actions: 'Akcije',
    admin: 'Administrator',
    editor: 'Urednik',
    viewer: 'Pregledač',
    auditor: 'Revizor',
    activity: 'Aktivnost',
    timestamp: 'Vreme',
    user: 'Korisnik',
    action: 'Akcija',
    details: 'Detalji',
    ipAddress: 'IP adresa',
  },
  suppliersExt: {
    totalSuppliers: 'Ukupno dobavljača',
    active: 'Aktivni',
    criticalTier: 'Kritični nivo',
    nonCompliant: 'Neusklađeni',
    expiringContract: 'Ističe ugovor',
    searchSuppliers: 'Pretraži dobavljače...',
    allCategories: 'Sve kategorije',
    allRiskTiers: 'Svi nivoi rizika',
    allStatuses: 'Svi statusi',
    addSupplier: 'Dodaj dobavljača',
    noSuppliers: 'Još nema registrovanih dobavljača',
    showing: 'Prikazano',
  },
  common: {
    of: 'od',
    showing: 'Prikazano',
    noResults: 'Nema rezultata',
    loading: 'Učitavanje...',
    error: 'Greška',
    success: 'Uspešno',
    warning: 'Upozorenje',
    info: 'Informacija',
    clickToEdit: 'Kliknite za izmenu',
    total: 'Ukupno',
    items: 'stavki',
    confirmDelete: 'Da li ste sigurni da želite da obrišete?',
    date: 'Datum',
    actions: 'Akcije',
  },
};

// English translations
const en: Translations = {
  nav: {
    overview: 'Overview',
    programs: 'Programs',
    controls: 'Controls',
    risks: 'Risks',
    assets: 'Assets',
    policies: 'Policies',
    suppliers: 'Suppliers',
    audits: 'Audits',
    incidents: 'Incidents',
    analytics: 'Analytics',
    settings: 'Settings',
    proof: 'Proof',
    integrations: 'Integrations',
    ai_assistant: 'AI Assistant',
    // Enterprise modules
    training: 'Training',
    automation: 'Automation',
    cloud: 'Cloud Security',
    saas: 'SaaS Security',
    endpoints: 'Endpoints',
    pentest: 'Pentest',
    trustCenter: 'Trust Center',
    aiAssistant: 'GRC Assistant',
    executive: 'Executive Report',
  },
  actions: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    create: 'Create',
    update: 'Update',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    import: 'Import',
    download: 'Download',
    upload: 'Upload',
    back: 'Back',
    next: 'Next',
    finish: 'Finish',
    close: 'Close',
    confirm: 'Confirm',
    apply: 'Apply',
    reset: 'Reset',
    clear: 'Clear',
    select: 'Select',
    selectAll: 'Select All',
    view: 'View',
    details: 'Details',
    generatePolicy: 'Generate Policy',
    linkEvidence: 'Link Evidence',
    runAnalysis: 'Run Analysis',
    remove: 'Remove',
    description: 'Description',
    showAll: 'Show All',
    showLess: 'Show Less',
    fix: 'Fix',
    required: 'Required',
  },
  status: {
    compliant: 'Compliant',
    partial: 'Partial',
    nonCompliant: 'Non-Compliant',
    notAssessed: 'Not Assessed',
    notApplicable: 'Not Applicable',
    implemented: 'Implemented',
    inProgress: 'In Progress',
    notStarted: 'Not Started',
    completed: 'Completed',
    healthy: 'Healthy',
    atRisk: 'At Risk',
    critical: 'Critical',
    active: 'Active',
    inactive: 'Inactive',
    open: 'Open',
    closed: 'Closed',
    mitigated: 'Mitigated',
    accepted: 'Accepted',
  },
  dashboard: {
    title: 'Dashboard',
    welcome: 'Welcome',
    totalControls: 'Total Controls',
    applicable: 'Applicable',
    partial: 'Partial',
    notApplicable: 'Not Applicable',
    complianceScore: 'Compliance Score',
    healthStatus: 'Health Status',
    implementation: 'Implementation',
    testing: 'Testing',
    freshness: 'Freshness',
    recentActivity: 'Recent Activity',
    nextActions: 'Next Best Actions',
    aiAssistant: 'AI Assistant',
    askAnything: 'Ask me anything...',
    topRisks: 'Top Risks',
    upcomingAudits: 'Upcoming Audits',
    noOpenRisks: 'No open risks',
    noScheduledAudits: 'No scheduled audits',
    soa: 'Statement of Applicability',
    automatedChecks: 'Automated Checks',
    manualChecks: 'Manual Checks',
    aiAssessment: 'AI Assessment',
    recommendations: 'Recommendations',
    noSummaryAvailable: 'No summary available.',
    priorityHigh: 'HIGH',
    priorityMedium: 'MEDIUM',
    priorityLow: 'LOW',
  },
  programs: {
    title: 'Programs',
    addNew: 'Add New Program',
    selectFramework: 'Select Framework',
    name: 'Name',
    health: 'Health',
    definition: 'Definition',
    owner: 'Owner',
    createdDate: 'Created Date',
    requirements: 'Requirements',
    controls: 'Controls',
    labels: 'Labels',
    proofLinks: 'Proof Links',
    startQuestionnaire: 'Start Questionnaire',
    viewDetails: 'View Details',
  },
  controls: {
    title: 'Controls',
    code: 'Code',
    description: 'Description',
    guidance: 'Guidance',
    severity: 'Severity',
    checkType: 'Check Type',
    implementationStatus: 'Implementation Status',
    testingStatus: 'Testing Status',
    owner: 'Owner',
    linkedPolicies: 'Linked Policies',
    evidence: 'Evidence',
    lastReview: 'Last Review',
    nextReview: 'Next Review',
    applicability: 'Applicability',
    justification: 'Justification',
    automated: 'Automated',
    manual: 'Manual',
    hybrid: 'Hybrid',
    linkPolicyAsEvidence: 'Link Policy as Evidence',
    noPoliciesAvailable: 'No policies available. Create one in Policy Generator.',
  },
  risks: {
    title: 'Risks',
    register: 'Risk Register',
    summary: 'Risk Summary',
    residualRisk: 'Residual Risk',
    riskHealth: 'Risk Health',
    heatmap: 'Risk Heatmap',
    likelihood: 'Likelihood',
    impact: 'Impact',
    treatment: 'Treatment',
    inherentRisk: 'Inherent Risk',
    toleranceLevel: 'Tolerance Level',
    veryHigh: 'Very High',
    high: 'High',
    moderate: 'Moderate',
    low: 'Low',
    veryLow: 'Very Low',
  },
  assets: {
    title: 'Assets',
    inventory: 'Asset Inventory',
    name: 'Name',
    type: 'Type',
    category: 'Category',
    value: 'Value',
    location: 'Location',
    custodian: 'Custodian',
    classification: 'Classification',
  },
  policies: {
    title: 'Policies',
    generate: 'Generate Policy',
    template: 'Template',
    version: 'Version',
    approvedBy: 'Approved By',
    effectiveDate: 'Effective Date',
    reviewDate: 'Review Date',
    linkedControls: 'Linked Controls',
    attachAsEvidence: 'Attach as Evidence',
  },
  audits: {
    title: 'Audits',
    name: 'Name',
    type: 'Type',
    timeline: 'Timeline',
    requests: 'Requests',
    findings: 'Findings',
    internal: 'Internal',
    external: 'External',
    certification: 'Certification',
    surveillance: 'Surveillance',
    aiPowered: 'AI Powered',
    startDeepAudit: 'Start Deep Audit',
    analyzeAndGenerate: 'Analyze & Generate',
    history: 'Audit History',
    noAuditsYet: 'No audits performed yet.',
    executiveSummary: 'Executive Summary',
    recommendations: 'Recommendations',
    recommendation: 'Recommendation',
    comments: 'Comments',
    notes: 'Notes',
    noCommentsYet: 'No comments yet.',
    addComment: 'Add a comment...',
    scheduledReviews: 'Scheduled Reviews',
    deepAuditProgress: 'Deep Audit Progress',
  },
  suppliers: {
    title: 'Suppliers',
    management: 'Supplier Management',
    name: 'Name',
    riskLevel: 'Risk Level',
    contractDate: 'Contract Date',
    reviewDate: 'Review Date',
    services: 'Services',
    certifications: 'Certifications',
    dueDiligence: 'Due Diligence',
  },
  incidents: {
    title: 'Incidents',
    report: 'Incident Report',
    severity: 'Severity',
    reportedDate: 'Reported Date',
    resolvedDate: 'Resolved Date',
    affectedAssets: 'Affected Assets',
    rootCause: 'Root Cause',
    lessonsLearned: 'Lessons Learned',
  },
  questionnaire: {
    title: 'Questionnaire',
    welcome: 'Welcome to the Applicability Assessment Questionnaire',
    applicabilityCheck: 'Applicability Check',
    questionOf: 'Question {current} of {total}',
    progress: 'Progress',
    selectOption: 'Select an option',
    yes: 'Yes',
    no: 'No',
    notSure: 'Not Sure',
    skip: 'Skip',
    results: 'Results',
    applicableControls: 'Applicable Controls',
    recommendations: 'Recommendations',
    complete: 'Complete',
    excludedControls: 'Excluded Controls',
    viewResults: 'View Results',
    applyRecommendations: 'Apply Recommendations',
  },
  penalties: {
    title: 'Penalties',
    warning: 'Warning: Potential Penalties',
    potentialFines: 'Potential Fines',
    maxFine: 'Maximum Fine',
    legalBasis: 'Legal Basis',
    nonCompliantControls: 'Non-Compliant Controls',
    resolveNow: 'Resolve Now',
    riskLevel: 'Risk Level',
  },
  settings: {
    title: 'Settings',
    users: 'Users',
    groups: 'Groups',
    permissions: 'Permissions',
    auditTrail: 'Audit Trail',
    language: 'Language',
    theme: 'Theme',
    notifications: 'Notifications',
    integrations: 'Integrations',
  },
  ai: {
    title: 'AI Analysis',
    analysis: 'Analysis',
    recommendations: 'Recommendations',
    generateReport: 'Generate Report',
    askQuestion: 'Ask a Question',
    priority: 'Priority',
    confidence: 'Confidence',
  },
  freshness: {
    fresh: 'Fresh',
    expired: 'Expired',
    unknown: 'Unknown',
    notSet: 'Not Set',
    daysAgo: '{days} days ago',
    daysRemaining: '{days} days remaining',
  },
  explore: {
    title: 'Explore',
    scopes: 'Scopes',
    members: 'Members',
    domains: 'Domains',
    priority: 'Priority',
  },
  proof: {
    title: 'Proof',
    addProof: 'Add Proof',
    uploadFile: 'Upload File',
    linkPolicy: 'Link Policy',
    linkControl: 'Link Control',
    freshUntil: 'Fresh Until',
    uploadedBy: 'Uploaded By',
    uploadedOn: 'Uploaded On',
    linkEvidence: 'Link Evidence',
    policies: 'Policies',
    existingEvidence: 'Existing Evidence',
    uploadNew: 'Upload New',
    selectFile: 'Select File',
    evidenceType: 'Evidence Type',
    uploadEvidence: 'Upload Evidence',
    linkSelected: 'Link Selected',
    linkedEvidence: 'Linked Evidence',
    manage: 'Manage',
    manageEvidence: 'Manage Evidence',
  },
  analytics: {
    title: 'GRC Analytics',
    overview: 'Overview',
    comparison: 'Comparison',
    maturity: 'Maturity',
    refresh: 'Refresh',
    export: 'Export',
    overallCompliance: 'Overall Compliance',
    averageAcross: 'Average across all frameworks',
    activeFrameworks: 'Active Frameworks',
    controlsImplemented: 'Controls Implemented',
    pendingActions: 'Pending Actions',
    globalStatus: 'Global Implementation Status',
    complianceByFramework: 'Compliance by Framework',
    controlsBreakdown: 'Controls Breakdown by Framework',
    multiDimensional: 'Multi-Dimensional Framework Comparison',
    maturityAssessment: 'Compliance Maturity Assessment',
    priorityRecommendations: 'Priority Recommendations',
    noData: 'No Analytics Data Available',
    completeWizard: 'Complete the AI Applicability Wizard for at least one framework to see analytics.',
    implemented: 'Implemented',
    inProgress: 'In Progress',
    notStarted: 'Not Started',
    level: 'Level',
    optimized: 'Optimized',
    managed: 'Managed',
    defined: 'Defined',
    developing: 'Developing',
    initial: 'Initial',
  },
  aiAdvisor: {
    title: 'AI Implementation Advisor',
    analyzing: 'AI Advisor is analyzing control',
    technicalControls: 'Technical Controls',
    organizationalControls: 'Organizational Controls',
    implementationSteps: 'Implementation Steps',
    requiredDocumentation: 'Required Documentation',
    suggestedJustification: 'Suggested Justification',
    suggestedOwner: 'Suggested Owner',
    dismiss: 'Dismiss',
    applySuggestions: 'Apply Suggestions',
    error: 'Error',
    close: 'Close',
    notApplicable: 'None directly applicable; this control focuses on leadership and management commitment rather than specific technical measures.',
  },
  rbac: {
    title: 'User Management',
    users: 'Users',
    roles: 'Roles',
    permissions: 'Permissions',
    auditLog: 'Audit Log',
    addUser: 'Add User',
    editUser: 'Edit User',
    deleteUser: 'Delete User',
    userName: 'Username',
    email: 'Email',
    role: 'Role',
    lastLogin: 'Last Login',
    status: 'Status',
    actions: 'Actions',
    admin: 'Administrator',
    editor: 'Editor',
    viewer: 'Viewer',
    auditor: 'Auditor',
    activity: 'Activity',
    timestamp: 'Timestamp',
    user: 'User',
    action: 'Action',
    details: 'Details',
    ipAddress: 'IP Address',
  },
  suppliersExt: {
    totalSuppliers: 'Total Suppliers',
    active: 'Active',
    criticalTier: 'Critical Tier',
    nonCompliant: 'Non-Compliant',
    expiringContract: 'Expiring < 90d',
    searchSuppliers: 'Search suppliers...',
    allCategories: 'All Categories',
    allRiskTiers: 'All Risk Tiers',
    allStatuses: 'All Statuses',
    addSupplier: 'Add Supplier',
    noSuppliers: 'No suppliers registered yet',
    showing: 'Showing',
  },
  common: {
    of: 'of',
    showing: 'Showing',
    noResults: 'No results',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    info: 'Info',
    clickToEdit: 'Click to edit',
    total: 'Total',
    items: 'items',
    confirmDelete: 'Are you sure you want to delete?',
    date: 'Date',
    actions: 'Actions',
  },
};

// Translation map
const translations: Record<Language, Translations> = { sr, en };

// Context type
interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  switchLanguage: () => void;
}

// Create context
const I18nContext = createContext<I18nContextType | undefined>(undefined);

// Provider component
interface I18nProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ 
  children, 
  defaultLanguage = 'sr' 
}) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('grc-language');
    return (saved === 'sr' || saved === 'en') ? saved : defaultLanguage;
  });

  useEffect(() => {
    localStorage.setItem('grc-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const switchLanguage = () => {
    setLanguageState(prev => prev === 'sr' ? 'en' : 'sr');
  };

  const value: I18nContextType = {
    language,
    setLanguage,
    t: translations[language],
    switchLanguage,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

// Hook for using translations
export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

// Helper function for template strings
export const formatString = (template: string, values: Record<string, string | number>): string => {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(values[key] || ''));
};

export default I18nProvider;
